import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import axiosInstance from '../../_helpers/axios_instance';
import { format } from 'date-fns';
import { formatIsoDateForFilename } from '../../_helpers/misc_functions';
import { isForLens, lensParams, selectLensGroupId, type LensMeta } from './groups_slice';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface CmeCredit {
  id: number;
  /** The item's record id, which the API stores and sends as a string. */
  lesson_number: string;
  title_of_activity: string;
  cme_certificate_awarded_at: string;
  created_at: string;
  [key: string]: unknown;
}

interface CmeCreditsState {
  credits: CmeCredit[];
  fetchCreditsStatus: AsyncStatus;
  fetchCreditsError: string | string[] | null;
  /** The group lens (selectLensGroupId) the credits are for. */
  lensGroupId: number | null;
  claimCreditStatus: AsyncStatus;
  claimCreditError: string | string[] | null;
  downloadCertificateStatus: AsyncStatus;
  downloadCertificateError: string | string[] | null;
}

const initialState: CmeCreditsState = {
  credits: [],
  fetchCreditsStatus: 'idle',
  fetchCreditsError: null,
  lensGroupId: null,
  claimCreditStatus: 'idle',
  claimCreditError: null,
  downloadCertificateStatus: 'idle',
  downloadCertificateError: null,
};

// The credits follow the group lens, so each answer records the lens it was
// asked for and one for another lens is dropped.
export const getCmeCredits = createAsyncThunk<
  CmeCredit[], void, { state: RootState; fulfilledMeta: LensMeta; rejectedMeta: LensMeta }
>(
  'cmeCredits/fetchCredits',
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    const meta = { lensGroupId: selectLensGroupId(getState()) };
    try {
      const response = meta.lensGroupId === null
        ? await axiosInstance.get('/api/cme_credits/')
        : await axiosInstance.get('/api/cme_credits/', { params: lensParams(meta.lensGroupId) });
      return fulfillWithValue(response.data, meta);
    } catch (error: any) {
      if (error.response) return rejectWithValue(error.response.data.errors, meta);
      return rejectWithValue(error.message, meta);
    }
  },
  {
    condition: (_, { getState }) => getState().cme_credits.fetchCreditsStatus !== 'loading',
  }
);

export const claimCmeCredit = createAsyncThunk<CmeCredit, any, { state: RootState }>(
  'cmeCredits/claimCredit',
  async (creditData, { getState, rejectWithValue }) => {
    // A claim carries the lens its finishes were listed for: under the Demo
    // Group lens the API saves nothing, as the story's finishes aren't real.
    const lensGroupId = selectLensGroupId(getState());
    const body = {
      lesson_number: creditData.content_id,
      certificate_type: creditData.certificate_type,
      credential_type: creditData.credential_type,
      bias_response: creditData.bias_response,
      credits_awarded: creditData.credits_awarded,
      title_of_activity: creditData.title_of_activity,
      cme_certificate_awarded_at: format(new Date(), 'yyyy-MM-dd'),
      license_id: creditData.license_id,
      state: creditData.state,
    };
    try {
      const response = lensGroupId === null
        ? await axiosInstance.post('/api/cme_credits/', body)
        : await axiosInstance.post('/api/cme_credits/', body, { params: lensParams(lensGroupId) });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  {
    condition: (_, { getState }) => getState().cme_credits.claimCreditStatus !== 'loading',
  }
);

export const downloadCertificate = createAsyncThunk<void, any, { state: RootState }>(
  'cmeCredits/downloadCertificate',
  async (credit, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/cme_credits/certificate/${credit.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${credit.title_of_activity}-${formatIsoDateForFilename(credit.cme_certificate_awarded_at)}.pdf`);
      document.body.appendChild(link);
      link.click();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  {
    condition: (_, { getState }) => getState().cme_credits.downloadCertificateStatus !== 'loading',
  }
);

const cmeCreditsSlice = createSlice({
  name: 'cme_credits',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCmeCredits.pending, (state) => {
        state.fetchCreditsStatus = 'loading';
        state.fetchCreditsError = null;
      })
      .addCase(getCmeCredits.fulfilled, (state, action) => {
        if (!isForLens(action, state.lensGroupId)) return;
        state.fetchCreditsStatus = 'succeeded';
        state.credits = action.payload;
      })
      .addCase(getCmeCredits.rejected, (state, action) => {
        if (!isForLens(action, state.lensGroupId)) return;
        state.fetchCreditsStatus = 'failed';
        state.fetchCreditsError = (action.payload as string | string[]) ?? null;
      })
      .addCase(claimCmeCredit.pending, (state) => {
        state.claimCreditStatus = 'loading';
        state.claimCreditError = null;
      })
      .addCase(claimCmeCredit.fulfilled, (state, action) => {
        state.claimCreditStatus = 'succeeded';
        // Only a stored credit (it has an id) is a claim. Preview and demo
        // accounts get a 200 status object with no id and no credit behind
        // it; recording that would show the item as claimed until a reload.
        if (typeof action.payload?.id === 'number') state.credits.push(action.payload);
      })
      .addCase(claimCmeCredit.rejected, (state, action) => {
        state.claimCreditStatus = 'failed';
        state.claimCreditError = (action.payload as string | string[]) ?? null;
      })
      .addCase(downloadCertificate.pending, (state) => {
        state.downloadCertificateStatus = 'loading';
        state.downloadCertificateError = null;
      })
      .addCase(downloadCertificate.rejected, (state, action) => {
        state.downloadCertificateStatus = 'failed';
        state.downloadCertificateError = (action.payload as string | string[]) ?? null;
      })
      .addCase(downloadCertificate.fulfilled, (state) => {
        state.downloadCertificateStatus = 'succeeded';
        state.downloadCertificateError = null;
      });
  },
});

export default cmeCreditsSlice.reducer;

/**
 * Resets the credits when they were loaded for a lens other than
 * `lensGroupId`, so they read as not loaded and the pages that show them ask
 * again. The root reducer runs this after every action; with no lens it
 * changes nothing.
 */
export const keepLens = (state: CmeCreditsState, lensGroupId: number | null): CmeCreditsState =>
  (state.lensGroupId ?? null) === lensGroupId
    ? state
    : { ...state, credits: [], fetchCreditsStatus: 'idle', fetchCreditsError: null, lensGroupId };

export const selectAllCmeCredits = (state: { cme_credits: CmeCreditsState }) => state.cme_credits.credits;
export const selectCmeCreditsStatus = (state: { cme_credits: CmeCreditsState }) => state.cme_credits.fetchCreditsStatus;
export const selectCmeCreditsError = (state: { cme_credits: CmeCreditsState }) => state.cme_credits.fetchCreditsError;
export const selectClaimCreditStatus = (state: { cme_credits: CmeCreditsState }) => state.cme_credits.claimCreditStatus;
export const selectClaimCreditError = (state: { cme_credits: CmeCreditsState }) => state.cme_credits.claimCreditError;

/**
 * Whether a credit is saved on the server, and so has a certificate to download.
 * The Demo Group's story credits carry negative ids, and a preview claim comes
 * back as a status with no id.
 */
export const isStoredCredit = (credit: unknown): credit is CmeCredit => {
  const id = (credit as { id?: unknown } | null)?.id;
  return typeof id === 'number' && id > 0;
};

export const selectAllCmeCreditsForContent = (state: { cme_credits: CmeCreditsState }, record_id: number | string) =>
  state.cme_credits.credits.filter((credit) => String(credit.lesson_number) === String(record_id));

export const selectLatestCmeCreditForContent = (state: { cme_credits: CmeCreditsState }, record_id: number | string): CmeCredit | null => {
  const matching = selectAllCmeCreditsForContent(state, record_id);
  if (matching.length === 0) return null;
  return matching.reduce((latest, current) =>
    (latest.created_at > current.created_at ? latest : current)
  );
};
